import { NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Package, Receipt, Users, Mail, Image, Tags, FolderOpen, ShieldCheck, ScrollText } from "lucide-react";
import { useAuth } from "../../context/AppContext";
import { Loading } from "../../components/PageHeader";

const NAV = [
  ["/admin", "Overview", LayoutDashboard, "overview", ["view_analytics"]],
  ["/admin/products", "Products", Package, "products", ["manage_products"]],
  ["/admin/prices", "Prices", Tags, "prices", ["manage_prices"]],
  ["/admin/media", "Media library", FolderOpen, "media", ["manage_media"]],
  ["/admin/orders", "Orders", Receipt, "orders", ["manage_orders"]],
  ["/admin/users", "Users", Users, "users", ["manage_users"]],
  ["/admin/roles", "Roles", ShieldCheck, "roles", ["manage_roles"]],
  ["/admin/messages", "Messages", Mail, "messages", ["manage_messages"]],
  ["/admin/site", "Site content", Image, "site", ["manage_site_content"]],
  ["/admin/audit", "Audit log", ScrollText, "audit", ["manage_users", "manage_roles"]],
];

const Forbidden = ({ msg }) => (
  <div className="py-24" data-testid="admin-forbidden">
    <div className="eyebrow">403</div>
    <h1 className="mt-4 font-display text-4xl">No access</h1>
    <p className="mt-4 muted">{msg}</p>
  </div>
);

export const AdminTitle = ({ eyebrow, title, children }) => (
  <div className="mb-10 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
    <div>
      <div className="eyebrow">{eyebrow}</div>
      <h1 className="mt-3 font-display text-4xl tracking-tight">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-3">{children}</div>}
  </div>
);

export const Pill = ({ children, tone = "muted", testid }) => {
  const tones = {
    gold: { color: "var(--wb-gold)", borderColor: "var(--wb-line-accent)" },
    green: { color: "#6FBF8E", borderColor: "rgba(111,191,142,.35)" },
    red: { color: "#E07A6B", borderColor: "rgba(224,122,107,.4)" },
    muted: { color: "var(--wb-muted)", borderColor: "var(--wb-line-strong)" },
  };
  return <span data-testid={testid} className="inline-block border px-2 py-0.5 font-mono text-[0.6rem] uppercase tracking-[0.18em]" style={tones[tone]}>{children}</span>;
};

export const ORDER_TONE = { paid: "gold", shipped: "green", pending: "muted", cancelled: "red" };

export const ErrorBox = ({ msg }) => <div className="border border-red-500/40 p-4 text-sm text-red-400" data-testid="admin-error">{msg}</div>;

export default function AdminLayout() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login?next=/admin" replace />;
  const perms = user.permissions || [];
  if (!perms.length) return <div className="wb-container"><Forbidden msg="Your account does not have access to the studio dashboard." /></div>;
  const allowed = NAV.filter((n) => n[4].some((p) => perms.includes(p)));
  const current = [...NAV].sort((x, y) => y[0].length - x[0].length).find((n) => pathname === n[0] || pathname.startsWith(`${n[0]}/`));
  if (pathname === "/admin" && !perms.includes("view_analytics") && allowed[0]) return <Navigate to={allowed[0][0]} replace />;
  const ok = !current || current[4].some((p) => perms.includes(p));
  return (
    <div className="wb-container grid gap-10 py-12 lg:grid-cols-[210px_1fr]" data-testid="admin-layout">
      <aside className="lg:sticky lg:top-28 h-fit">
        <div className="eyebrow mb-5">Studio admin</div>
        <nav className="flex gap-1 overflow-x-auto lg:flex-col">
          {allowed.map(([to, label, Icon, id]) => (
            <NavLink
              key={to} to={to} end={to === "/admin"} data-testid={`admin-nav-${id}`}
              className={({ isActive }) => `flex items-center gap-3 whitespace-nowrap border-l-2 px-4 py-2.5 text-sm transition-colors ${isActive ? "border-[color:var(--wb-gold)] text-wb-gold bg-wb-card" : "border-transparent muted hover:text-wb-text"}`}
            >
              <Icon size={15} strokeWidth={1.5} /> {label}
            </NavLink>
          ))}
        </nav>
      </aside>
      <section className="min-w-0">{ok ? <Outlet /> : <Forbidden msg="Your role does not include permission for this section." />}</section>
    </div>
  );
}
